import env from "@/configuration/env";
import store from "@/stores";
import router from "@/routes";
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";

// state
let userPool;
let user;
let sessionUserAttributes;

// init
const init = async () => {
  if (sessionStorage) store.dispatch("cognito/initPinMode", sessionStorage.pinMode ? JSON.parse(sessionStorage.pinMode) : {});
  // user pool
  userPool = new CognitoUserPool({
    UserPoolId: env.cognito_user_pool_id,
    ClientId: env.cognito_user_pool_client_id,
  });

  // user
  user = userPool.getCurrentUser();

  // init
  if (user) {
    user.getSession((err, session) => {
      if (session) store.dispatch("cognito/setIdToken", session.idToken);
    });
  }

  // sync store
  syncStore();
  setInterval(() => syncStore(), 5 * 1000);
};

// error messages
function getErrorMessage(res) {
  if (res.message === "User password cannot be reset in the current state.") return "User password cannot be reset in the current state.";

  switch (res.code) {
    case "UserNotFoundException":
    case "NotAuthorizedException":
      return "Invalid login information.";
    case "UserNotConfirmedException":
      return "You must verify your account before you can login. Check your email for a verification link.";
    case "LimitExceededException":
      return "Attempt limit exceeded, please try again later. If you need immediate assistance, please contact support.";
    default:
      return res.message ? res.message : "An unknown error has occured.";
  }
}

// callbacks
function createCallbacks(options) {
  return {
    onSuccess(res) {
      // store
      if (res && res.idToken) store.dispatch("cognito/setIdToken", res.idToken);
      // callback
      if (options.onComplete) options.onComplete();
      if (options.onSuccess) options.onSuccess();
    },
    onFailure(res) {
      // message
      const message = getErrorMessage(res);
      // callback
      if (options.onComplete) options.onComplete();
      if (options.onFailure) options.onFailure({ message });
    },
    newPasswordRequired(userAttributes) {
      delete userAttributes.email_verified;
      sessionUserAttributes = userAttributes;
      const returnUrl = new URL(document.location).searchParams.get("returnUrl");
      router.push(`/signin/new-password${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ""}`);
    },
  };
}

// features
function signIn(options) {
  // callbacks
  const callbacks = createCallbacks(options);

  // user
  user = new CognitoUser({
    Username: options.email,
    Pool: userPool,
  });

  // authentication details
  const authDetails = new AuthenticationDetails({
    Username: options.email,
    Password: options.password,
  });

  // new password workflow
  if (options.newPassword) {
    user.completeNewPasswordChallenge(options.newPassword, sessionUserAttributes, callbacks);
    return;
  }

  // sign in workflow
  user.authenticateUser(authDetails, callbacks);
}

function signOut(options) {
  // callbacks
  const callbacks = createCallbacks(options);

  // store
  store.dispatch("cognito/setIdToken", null);
  store.dispatch("organization/clearOrganization", null);
  store.dispatch("role/clearRoles", null);

  // cognito
  if (user) {
    user.signOut();
    user = null;
    sessionUserAttributes = null;
  }

  // callback
  callbacks.onSuccess();
}

function forgotPassword(options) {
  // callbacks
  const callbacks = createCallbacks(options);

  // user
  const tempUser = new CognitoUser({
    Username: options.email,
    Pool: userPool,
  });

  // forgot password verify
  if (options.verificationCode && options.newPassword) {
    tempUser.confirmPassword(options.verificationCode, options.newPassword, callbacks);
    return;
  }

  // forgot password
  tempUser.forgotPassword(callbacks);
  return;
}

function changePassword(options) {
  // ensure session is valid before changing password
  user.getSession(function(sessionErr) {
    if (sessionErr) {
      options.onComplete();
      options.onFailure(sessionErr.message);
      return;
    }
    user.changePassword(options.oldPassword, options.newPassword, function(err) {
      if (err) {
        options.onComplete();
        options.onFailure(err.message);
        return;
      }
      options.onComplete();
      options.onSuccess();
    });
  });
  return;
}

function handleNewPassword(options) {
  var attributes = sessionUserAttributes;
  delete attributes.phone_number;
  user.completeNewPasswordChallenge(options.newPassword, attributes, createCallbacks(options));
}

function syncStore() {
  if (!user) {
    store.dispatch("cognito/setIdToken", null);
    return;
  }

  if (!user.signInUserSession) {
    return;
  }

  user.getSession((err, session) => {
    if (!session) {
      router.push("/signout");
      return;
    }

    // now
    var now = new Date().getTime() / 1000;

    // is expired
    var expiration = session.idToken.getExpiration();
    var isExpired = expiration < now;
    if (isExpired) {
      router.push("/signout");
      return;
    }

    // refresh
    var ttl = expiration - now;
    var shouldRefresh = ttl < 1800;
    if (shouldRefresh) {
      var refreshToken = session.getRefreshToken();
      user.refreshSession(refreshToken, (err, newSession) => store.dispatch("cognito/setIdToken", newSession.idToken));
      return;
    }

    // sync
    store.dispatch("cognito/setIdToken", session.idToken);
  });
}

// exports
export default {
  signIn,
  signOut,
  forgotPassword,
  changePassword,
  handleNewPassword,
  init: init,
};
